import posthog from 'posthog-js';
import _omitBy from 'lodash/omitBy';
import _isNil from 'lodash/isNil';

/**
 * PostHog runs in parallel with Magnus (dual-send). Magnus stays the primary
 * analytics service and is untouched; every event that goes to Magnus via the
 * `Analytics` module is mirrored here with the same name and properties.
 *
 * The key is per-environment (`REACT_APP_POSTHOG_API_KEY` in `.env.*`). When it
 * is empty (e.g. the prod project is not created yet) `init` is a no-op and all
 * other methods stay silent — deploying without a key is safe.
 */

// Reverse proxy against ad-blockers — a neutral subdomain forwards to PostHog
// (US cloud), so events aren't dropped by blocklists.
const POSTHOG_HOST = 'https://r.hairio.app';
// UI host stays the real PostHog domain so links back to the app work.
const POSTHOG_UI_HOST = 'https://us.posthog.com';

// One PostHog project serves every environment — there is no separate stage
// project. Events are tagged with `app_env` so prod metrics don't mix with test
// traffic. Same property name/values as the PWA and the other funnels, so
// filters and dashboards line up everywhere. Mapping by hostname keeps this
// independent of how the bundle is built.
const APP_ENVS: Record<string, string> = {
  'quiz.hairio.app': 'prod',
  'stage.hairio.app': 'stage',
};
const appEnv = APP_ENVS[window.location.hostname] || 'dev';

let enabled = false;

function clean(params?: Record<string, unknown>) {
  return params ? _omitBy(params, _isNil) : params;
}

export const PostHog = {
  init(apiKey: string | undefined) {
    if (!apiKey || enabled) {
      return;
    }

    posthog.init(apiKey, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      // The anonymous distinct_id is kept in a cookie on the top-level domain
      // (.hairio.app), so the funnel (quiz.hairio.app) and the PWA
      // (app.hairio.app) share one anonymous identity — the "funnel → PWA"
      // chain is continuous on the same device out of the box.
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      // Events and screens are sent manually with the same names/params as
      // Magnus; DOM autocapture would produce a second, inconsistent stream.
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      // Session replay records the whole canvas without masking — off until a
      // dedicated privacy decision.
      disable_session_recording: true,
      // Tag `app_env` per event at send time. NOT via register(): that persists
      // it to the cross-subdomain cookie on .hairio.app, so a value set while
      // visiting stage would leak onto prod events for anyone who visits both
      // (QA, devs, owner) — exactly where the label gets looked at. `before_send`
      // adds the property to the event being sent and stores nothing.
      before_send: (event) => {
        if (event && event.properties) {
          event.properties.app_env = appEnv;
        }
        // Must return the event — returning undefined drops it.
        return event;
      },
    });

    // Clear any app_env that a previous register() may have left stuck in the
    // shared .hairio.app cookie. cross_subdomain_cookie stays on — it's what
    // glues the anonymous id across funnel → PWA.
    posthog.unregister('app_env');

    enabled = true;
  },

  capture(event: string, params?: Record<string, unknown>) {
    if (!enabled) return;
    posthog.capture(event, clean(params));
  },

  setPersonProperties(props: Record<string, unknown>) {
    if (!enabled) return;
    posthog.setPersonProperties(clean(props));
  },
};
